import { useState, useRef, useCallback, useEffect } from 'react';
import { parseWorkoutFromContent, parseProgramFromContent, WorkoutData, GoalsData, ProgramData } from '@/lib/chatParsers';
import { toast } from 'sonner';

export interface StreamingMessage {
  id: string;
  role: 'assistant';
  content: string;
  workout?: WorkoutData | null;
  goals?: GoalsData | null;
  program?: ProgramData | null;
}

interface StreamingState {
  isLoading: boolean;
  isStreaming: boolean;
  streamingMessage: StreamingMessage | null;
}

interface UseCoachStreamingOptions {
  onComplete: (content: string, meta?: { systemError?: boolean; errorType?: string | null }) => void | Promise<void>;
  onRetry?: () => void;
  maxRetries?: number;
  timeoutMs?: number;
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/coach-chat`;

export function useCoachStreaming({ onComplete, onRetry, maxRetries = 2, timeoutMs = 45000 }: UseCoachStreamingOptions) {
  const [state, setState] = useState<StreamingState>({
    isLoading: false,
    isStreaming: false,
    streamingMessage: null,
  });

  // Use refs to avoid closure issues and reduce memory
  const fullContentRef = useRef('');
  const abortControllerRef = useRef<AbortController | null>(null);
  const retryCountRef = useRef(0);
  const isStreamingRef = useRef(false);
  const errorTypeRef = useRef<string | null>(null);

  // Cleanup function to free memory
  const cleanup = useCallback(() => {
    fullContentRef.current = '';
  }, []);

  const cancelStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    // Persist any partial reply BEFORE cleanup zeroes the buffer — otherwise
    // canceling mid-stream silently deletes the in-flight assistant message.
    const partial = fullContentRef.current;
    if (partial) {
      try { void onComplete(partial); } catch { /* handled downstream */ }
    }
    cleanup();
    isStreamingRef.current = false;
    setState({ isLoading: false, isStreaming: false, streamingMessage: null });
  }, [cleanup, onComplete]);

  // Cleanup on unmount: abort fetch so we don't leak memory or leave ghost
  // streaming state if the user navigates away.
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      isStreamingRef.current = false;
    };
  }, []);


  const sendMessage = useCallback(async (
    messages: Array<{ role: string; content: string }>,
    userContext: Record<string, unknown>,
    accessToken: string,
    imageUrl?: string | null,
    memories?: Array<{ text: string; category: string }>,
  ) => {
    setState(prev => ({ ...prev, isLoading: true }));

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Soft timeout marker (no user-facing toast — keep UI silent during normal waits)
    const timeoutId = setTimeout(() => {
      // intentionally no-op; ThinkingIndicator already conveys progress
    }, 10000);

    // Track whether the abort came from the hard timeout (vs. user cancel via cancelStream).
    // The toast in the catch block uses this to show the right message.
    let timedOut = false;
    const hardTimeoutId = setTimeout(() => {
      timedOut = true;
      abortController.abort();
    }, timeoutMs);

    errorTypeRef.current = null;
    try {
      const response = await fetch(CHAT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          messages,
          userContext,
          imageUrl,
          memories,
          userLocalTime: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
          coachPersonality: (userContext as any)?.coachPersonality || null,
          primaryMotivation: (userContext as any)?.primaryMotivation || null,
        }),
        signal: abortController.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        clearTimeout(hardTimeoutId);
        
        if (response.status === 429) {
          try {
            const errorData = await response.json();
            if (errorData.code === 'GLOBAL_AI_LIMIT_REACHED') {
              toast.error('Daily AI limit reached. Resets at midnight.');
            } else if (errorData.retryAfter) {
              toast.error(`Slow down! Try again in ${errorData.retryAfter} seconds.`);
            } else if (errorData.limit) {
              toast.error(errorData.error || `Daily limit of ${errorData.limit} messages reached.`);
            } else {
              toast.error('Rate limit reached. Please wait a moment.');
            }
          } catch {
            toast.error('Rate limit reached. Please wait a moment.');
          }
        } else if (response.status === 402) {
          toast.error("Can't reach the coach right now. Try again in a minute.");
        } else {
          toast.error('Something went wrong. Please try again.');
        }
        
        setState(prev => ({ ...prev, isLoading: false }));
        return null;
      }

      // The edge function still returns HTTP 200 + an SSE fallback message when
      // the AI gateway itself fails, marked via X-Coach-Error-Type. We capture
      // that header so the persisted row can be tagged as message_type='system_error'
      // and excluded from the next turn's prompt context.
      errorTypeRef.current = response.headers.get('X-Coach-Error-Type');

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let assistantContent = '';
      let buffer = '';

      fullContentRef.current = '';
      isStreamingRef.current = true;

      // Wait-then-show: keep isLoading true and streamingMessage null until
      // the full response is ready. ChatContainer renders <ThinkingIndicator>
      // while isLoading && !hasStreamingMessage, then the persisted message
      // drops in once via chatHistory invalidation. No live typewriter bubble.

      while (reader) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
          const line = buffer.slice(0, newlineIndex).trim();
          buffer = buffer.slice(newlineIndex + 1);

          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6).trim();
            if (jsonStr === '[DONE]') continue;

            try {
              const parsed = JSON.parse(jsonStr);
              const content = parsed.choices?.[0]?.delta?.content;
              if (content) {
                assistantContent += content;
                fullContentRef.current = assistantContent;
              }
            } catch {
              // Malformed SSE line — skip it instead of re-prepending to the
              // buffer, which would otherwise loop forever on the same bad chunk.
              console.warn('[coach-stream] skipping malformed SSE line');
              continue;
            }
          }
        }
      }

      clearTimeout(hardTimeoutId);
      isStreamingRef.current = false;

      // Process remaining buffer
      if (buffer.trim()) {
        for (const line of buffer.split('\n')) {
          const trimmedLine = line.trim();
          if (trimmedLine.startsWith('data: ')) {
            const jsonStr = trimmedLine.slice(6).trim();
            if (jsonStr === '[DONE]') continue;
            try {
              const parsed = JSON.parse(jsonStr);
              const content = parsed.choices?.[0]?.delta?.content;
              if (content) {
                assistantContent += content;
                fullContentRef.current = assistantContent;
              }
            } catch { }
          }
        }
      }



      // Check for incomplete structured data
      if (assistantContent) {
        const hasWorkoutMarker = assistantContent.includes('```workout-json') || assistantContent.includes('```program-json');
        const parsedWorkout = parseWorkoutFromContent(assistantContent);
        const parsedProgram = parseProgramFromContent(assistantContent);
        const hasValidStructuredData = parsedWorkout !== null || parsedProgram !== null;

        if (hasWorkoutMarker && !hasValidStructuredData && retryCountRef.current < maxRetries) {
          retryCountRef.current++;
          cleanup();
          setState({ isLoading: false, isStreaming: false, streamingMessage: null });
          onRetry?.();
          return null;
        }

        retryCountRef.current = 0;
        const errorType = errorTypeRef.current;
        // Await persistence (and optimistic cache update) so the streaming
        // bubble does NOT blank out before the persisted message is in
        // chatHistory. Prevents the unmount/remount flicker on completion.
        try {
          await onComplete(assistantContent, errorType ? { systemError: true, errorType } : undefined);
        } catch { /* handled in onComplete */ }
      }

      errorTypeRef.current = null;

      cleanup();
      setState({ isLoading: false, isStreaming: false, streamingMessage: null });
      return assistantContent;
    } catch (error) {
      clearTimeout(timeoutId);
      clearTimeout(hardTimeoutId);
      isStreamingRef.current = false;

      // If we already received partial content, persist it so the user sees
      // the full text rather than a half-rendered ghost that disappears.
      const partial = fullContentRef.current;
      const isAbort = (error as Error).name === 'AbortError';

      if (isAbort) {
        if (timedOut) {
          toast.error('Coach took too long to respond. Please try again.');
        }
      } else {
        console.error('Chat error:', error);
        toast.error('Failed to connect. Please try again.');
      }

      // Flush partial content (only on timeout/network error, not on user cancel)
      if (partial && (!isAbort || timedOut)) {
        onComplete(partial);
      }

      cleanup();
      retryCountRef.current = 0;
      setState({ isLoading: false, isStreaming: false, streamingMessage: null });
      return null;
    }
  }, [maxRetries, onComplete, onRetry, timeoutMs, cleanup]);


  return {
    ...state,
    sendMessage,
    cancelStream,
  };
}
